import { useCallback, useEffect, useRef, useState } from "react";
import { TOKEN_KEY } from "@/lib/api";

const SLICE_MS = 7000; // longer slices => better Whisper accuracy, still near-realtime
const FRAME_BYTES = 32 * 1024; // ingress-safe websocket frame size
const FRAME_GAP_MS = 60; // throttle so the gateway does not drop a burst
const BARS = 56;

// ─────────────────────────────────────────────────────────────────────────────
// pickMime
// ─────────────────────────────────────────────────────────────────────────────
const pickMime = () => {
  const candidates = ["audio/webm;codecs=opus", "audio/webm", "audio/ogg;codecs=opus", "audio/mp4"];
  for (const c of candidates) {
    if (window.MediaRecorder?.isTypeSupported?.(c)) return c;
  }
  return "";
};

const wsUrl = (meetingId) => {
  const base = process.env.REACT_APP_BACKEND_URL.replace(/^http/, "ws");
  return `${base}/api/ws/meetings/${meetingId}/live?token=${encodeURIComponent(localStorage.getItem(TOKEN_KEY) || "")}`;
};

/**
 * Near-realtime microphone transcription.
 * One continuous MediaRecorder emits timeslices, so no audio is dropped between chunks.
 * The webm init segment lives only in the first blob; the backend re-attaches it to later slices.
 */
export function useMicTranscription() {
  const [status, setStatus] = useState("idle"); // idle|connecting|recording|paused|stopped|error
  const [segments, setSegments] = useState([]);
  const [levels, setLevels] = useState(() => new Array(BARS).fill(0.08));
  const [elapsed, setElapsed] = useState(0);
  const [speakers, setSpeakers] = useState([]);
  const [pending, setPending] = useState(0);
  const [error, setError] = useState("");
  const [result, setResult] = useState(null);

  const wsRef = useRef(null);
  const streamRef = useRef(null);
  const recorderRef = useRef(null);
  const ctxRef = useRef(null);
  const rafRef = useRef(null);
  const timerRef = useRef(null);
  const keepAliveRef = useRef(null);
  const activeRef = useRef(false);
  const chunkIxRef = useRef(0);
  const offsetRef = useRef(0); // audio seconds already emitted
  const queueRef = useRef(Promise.resolve());
  const userIdRef = useRef("");     // set by start(), used in every chunk_start
  const meetingIdRef = useRef(""); // set by start(), embedded in every chunk_start
  const supported = typeof navigator !== "undefined" && !!navigator.mediaDevices?.getUserMedia && !!window.MediaRecorder;

  const teardown = useCallback(() => {
    activeRef.current = false;
    clearInterval(timerRef.current);
    clearInterval(keepAliveRef.current);
    cancelAnimationFrame(rafRef.current);
    try { if (recorderRef.current && recorderRef.current.state !== "inactive") recorderRef.current.stop(); } catch (e) { void e; }
    recorderRef.current = null;
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
    try { ctxRef.current?.close(); } catch (e) { void e; }
    ctxRef.current = null;
    setLevels(new Array(BARS).fill(0.08));
  }, []);

  useEffect(() => () => { teardown(); try { wsRef.current?.close(); } catch (e) { void e; } }, [teardown]);

  const meter = (stream) => {
    const AC = window.AudioContext || window.webkitAudioContext;
    const ctx = new AC();
    ctxRef.current = ctx;
    const analyser = ctx.createAnalyser();
    analyser.fftSize = 512;
    analyser.smoothingTimeConstant = 0.75;
    ctx.createMediaStreamSource(stream).connect(analyser);
    const buf = new Uint8Array(analyser.frequencyBinCount);
    const loop = () => {
      analyser.getByteFrequencyData(buf);
      const step = Math.floor(buf.length / BARS) || 1;
      const next = new Array(BARS);
      for (let i = 0; i < BARS; i++) {
        let sum = 0;
        for (let j = 0; j < step; j++) sum += buf[i * step + j] || 0;
        next[i] = Math.max(0.08, Math.min(1, (sum / step / 255) * 2.4));
      }
      setLevels(next);
      rafRef.current = requestAnimationFrame(loop);
    };
    loop();
  };

  // Frames are paced and queued so two slices never interleave on the wire.
  const sendSlice = useCallback((blob) => {
    const ix = chunkIxRef.current++;
    const t0 = offsetRef.current;
    offsetRef.current += SLICE_MS / 1000;
    setPending((p) => p + 1);
    queueRef.current = queueRef.current.then(async () => {
      const ws = wsRef.current;
      if (!ws || ws.readyState !== 1) { setPending((p) => Math.max(0, p - 1)); return; }
      const buf = await blob.arrayBuffer();
      ws.send(JSON.stringify({
        action: "chunk_start",
        size: buf.byteLength,
        duration: SLICE_MS / 1000,
        t0,
        first: ix === 0,
        user_id: userIdRef.current,
        meeting_id: meetingIdRef.current,
      }));
      for (let off = 0; off < buf.byteLength; off += FRAME_BYTES) {
        if (ws.readyState !== 1) { setPending((p) => Math.max(0, p - 1)); return; }
        ws.send(buf.slice(off, Math.min(off + FRAME_BYTES, buf.byteLength)));
        if (off + FRAME_BYTES < buf.byteLength) await new Promise((r) => setTimeout(r, FRAME_GAP_MS));
      }
      if (ws.readyState === 1) ws.send(JSON.stringify({ action: "chunk_end" }));
    }).catch(() => setPending((p) => Math.max(0, p - 1)));
  }, []);

  const start = useCallback(async (meetingId, userId = "") => {
    setError(""); setResult(null); setSegments([]); setElapsed(0); setPending(0);
    chunkIxRef.current = 0; offsetRef.current = 0; queueRef.current = Promise.resolve();
    userIdRef.current = userId;
    meetingIdRef.current = meetingId;
    setStatus("connecting");

    let stream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({
        audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true, channelCount: 1 },
      });
    } catch (e) {
      setError("Akses mikrofon ditolak. Izinkan mikrofon pada browser lalu coba lagi.");
      setStatus("error");
      return false;
    }
    streamRef.current = stream;
    meter(stream);

    try {
      await new Promise((resolve, reject) => {
        const ws = new WebSocket(wsUrl(meetingId));
        ws.binaryType = "arraybuffer";
        wsRef.current = ws;
        ws.onopen = () => resolve();
        ws.onerror = () => reject(new Error("Koneksi realtime gagal. Periksa jaringan Anda."));
        ws.onmessage = (ev) => {
          const msg = JSON.parse(ev.data);
          if (msg.type === "ready") setSpeakers(msg.speakers || []);
          if (msg.type === "segment") { setSegments((p) => [...p, msg.segment]); }
          if (msg.type === "silence" || msg.type === "chunk_error") setPending((p) => Math.max(0, p - 1));
          if (msg.type === "segment") setPending((p) => Math.max(0, p - 1));
          if (msg.type === "finished") { setResult(msg); setStatus("stopped"); }
          if (msg.type === "error") { setError(msg.message); setStatus("error"); }
        };
        ws.onclose = () => { if (activeRef.current) { activeRef.current = false; setStatus("stopped"); } };
      });
    } catch (e) {
      setError(e.message);
      setStatus("error");
      teardown();
      return false;
    }

    const mimeType = pickMime();
    let rec;
    try {
      rec = new MediaRecorder(streamRef.current, {
        ...(mimeType ? { mimeType } : {}),
        audioBitsPerSecond: 64000,
      });
    } catch (e) {
      setError("Perekaman audio tidak didukung browser ini.");
      setStatus("error");
      teardown();
      return false;
    }
    recorderRef.current = rec;
    rec.ondataavailable = (ev) => { if (ev.data && ev.data.size > 1200) sendSlice(ev.data); };
    rec.onerror = () => { setError("Perekaman terhenti oleh browser."); setStatus("error"); };
    rec.start(SLICE_MS); // continuous recording, one blob per slice — no gaps

    activeRef.current = true;
    setStatus("recording");
    timerRef.current = setInterval(() => setElapsed((s) => s + 1), 1000);
    keepAliveRef.current = setInterval(() => {
      try { wsRef.current?.readyState === 1 && wsRef.current.send(JSON.stringify({ action: "ping" })); } catch (e) { void e; }
    }, 20000);
    return true;
  }, [sendSlice, teardown]);

  const pause = useCallback(() => {
    clearInterval(timerRef.current);
    try { recorderRef.current?.state === "recording" && recorderRef.current.pause(); } catch (e) { void e; }
    setStatus("paused");
  }, []);

  const resume = useCallback(() => {
    try { recorderRef.current?.state === "paused" && recorderRef.current.resume(); } catch (e) { void e; }
    setStatus("recording");
    timerRef.current = setInterval(() => setElapsed((s) => s + 1), 1000);
  }, []);

  const stop = useCallback(() => {
    activeRef.current = false;
    clearInterval(timerRef.current);
    clearInterval(keepAliveRef.current);
    try { if (recorderRef.current && recorderRef.current.state !== "inactive") recorderRef.current.stop(); } catch (e) { void e; }
    setStatus("stopped");
    // let the final slice finish uploading before closing the socket
    queueRef.current = queueRef.current.then(async () => {
      await new Promise((r) => setTimeout(r, 400));
      try { wsRef.current?.readyState === 1 && wsRef.current.send(JSON.stringify({ action: "stop" })); } catch (e) { void e; }
      teardown();
    });
  }, [teardown]);

  const reset = useCallback(() => {
    teardown();
    try { wsRef.current?.close(); } catch (e) { void e; }
    wsRef.current = null;
    setStatus("idle"); setSegments([]); setElapsed(0); setResult(null); setError(""); setPending(0);
  }, [teardown]);

  return { supported, status, segments, levels, elapsed, speakers, pending, error, result, sliceSeconds: SLICE_MS / 1000, start, pause, resume, stop, reset };
}
